import { z } from "zod";
import { toMinutes } from "./workSchedule";

const TIME_REGEX = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
const WORK_DAY_VALUES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

function startOfToday(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

export const scheduleAmendmentSchema = z
  .object({
    effective_date: z.date({ message: "validation.effective_date_required" }).nullable(),
    working_days_list: z
      .array(z.enum(WORK_DAY_VALUES, { message: "validation.invalid_working_day" }))
      .min(1, "validation.working_days_min")
      .max(6, "validation.working_days_max"),
    work_start_time: z.string().regex(TIME_REGEX, "validation.work_start_time_required"),
    work_end_time: z.string().regex(TIME_REGEX, "validation.work_end_time_required"),
    has_break: z.boolean(),
    break_start_time: z.string(),
    break_end_time: z.string(),
  })
  .superRefine((data, ctx) => {
    if (data.effective_date === null) {
      ctx.addIssue({ code: "custom", path: ["effective_date"], message: "validation.effective_date_required" });
    } else if (data.effective_date < startOfToday()) {
      ctx.addIssue({ code: "custom", path: ["effective_date"], message: "validation.effective_date_past" });
    }

    if (data.working_days_list.length === 1 && data.working_days_list[0] === "Sun") {
      ctx.addIssue({ code: "custom", path: ["working_days_list"], message: "validation.sunday_only" });
    }

    const hasBreakStart = TIME_REGEX.test(data.break_start_time);
    const hasBreakEnd = TIME_REGEX.test(data.break_end_time);
    if (data.has_break && !hasBreakStart) {
      ctx.addIssue({ code: "custom", path: ["break_start_time"], message: "validation.break_start_time_required" });
    }
    if (data.has_break && !hasBreakEnd) {
      ctx.addIssue({ code: "custom", path: ["break_end_time"], message: "validation.break_end_time_required" });
    }

    const hasValidWork = TIME_REGEX.test(data.work_start_time) && TIME_REGEX.test(data.work_end_time);
    if (!hasValidWork) return;

    const workStart = toMinutes(data.work_start_time);
    const workEnd = toMinutes(data.work_end_time);
    if (workEnd <= workStart) {
      ctx.addIssue({ code: "custom", path: ["work_end_time"], message: "validation.work_end_after_start" });
      return;
    }

    let breakMinutes = 0;
    if (data.has_break) {
      if (!hasBreakStart || !hasBreakEnd) return;

      const breakStart = toMinutes(data.break_start_time);
      const breakEnd = toMinutes(data.break_end_time);
      if (breakEnd <= breakStart) {
        ctx.addIssue({ code: "custom", path: ["break_end_time"], message: "validation.break_end_before_start" });
        return;
      }
      if (breakStart < workStart || breakEnd > workEnd) {
        ctx.addIssue({ code: "custom", path: ["break_start_time"], message: "validation.break_within_work_hours" });
        return;
      }
      breakMinutes = breakEnd - breakStart;
    }

    const netMinutes = workEnd - workStart - breakMinutes;
    if (netMinutes < 60 || netMinutes > 600) {
      ctx.addIssue({ code: "custom", path: ["work_end_time"], message: "validation.working_hours_range" });
    }
  });

export type ScheduleAmendmentFormValues = z.infer<typeof scheduleAmendmentSchema>;

const SALARY_MIN = 1;
const SALARY_MAX = 999_999_999;
const DECIMAL_PATTERN = /^\d+(\.\d+)?$/;
const WHOLE_TENGE_PATTERN = /^\d+(\.0+)?$/;

export function normalizeSalaryInput(raw: string): string {
  return raw.replace(/\s/g, "").replace(",", ".");
}

export const salaryAmendmentSchema = z.object({
  salary_amount: z
    .string()
    .transform(normalizeSalaryInput)
    .pipe(
      z
        .string()
        .min(1, "Common:amendment.validation.salary.required")
        .superRefine(function checkSalary(value, ctx) {
          if (value === "") return;
          if (!DECIMAL_PATTERN.test(value)) {
            ctx.addIssue({ code: "custom", message: "Common:amendment.validation.salary.invalid" });
            return;
          }
          const amount = Number(value);
          if (amount < SALARY_MIN || amount > SALARY_MAX) {
            ctx.addIssue({ code: "custom", message: "Common:amendment.validation.salary.range" });
            return;
          }
          if (!WHOLE_TENGE_PATTERN.test(value)) {
            ctx.addIssue({ code: "custom", message: "Common:amendment.validation.salary.wholeTenge" });
          }
        }),
    ),
});

function createDutiesSchema(requiredKey: string) {
  return z
    .array(z.string())
    .min(1, requiredKey)
    .refine(
      function allDutiesFilled(duties) {
        return duties.every((duty) => duty.trim() !== "");
      },
      "Common:amendment.validation.position.dutyBlank",
    );
}

export const positionAmendmentSchema = z.object({
  job_position_ru: z.string().trim().min(1, "Common:amendment.validation.position.ruRequired"),
  job_position_kk: z.string().trim().min(1, "Common:amendment.validation.position.kkRequired"),
  job_duties_ru: createDutiesSchema("Common:amendment.validation.position.dutiesRuRequired"),
  job_duties_kk: createDutiesSchema("Common:amendment.validation.position.dutiesKkRequired"),
});

export const clauseTextAmendmentSchema = z.object({
  new_clause_text_ru: z.string().trim().min(1, "Common:amendment.validation.clauseText.ruRequired"),
  new_clause_text_kk: z.string().trim().min(1, "Common:amendment.validation.clauseText.kkRequired"),
});

export function collectIssueMessages(error: z.ZodError): Record<string, string> {
  const messages: Record<string, string> = {};
  for (const issue of error.issues) {
    const field = String(issue.path[0]);
    if (!(field in messages)) messages[field] = issue.message;
  }
  return messages;
}

export type SalaryAmendmentFormValues = z.infer<typeof salaryAmendmentSchema>;
export type PositionAmendmentFormValues = z.infer<typeof positionAmendmentSchema>;
export type ClauseTextAmendmentFormValues = z.infer<typeof clauseTextAmendmentSchema>;
